import {$myFetch} from "."
import {ISupplierDeclinedReference, ISupplierReference} from "../../models"
import {CaratechType} from "../../models/bo"
import {Multilang} from "../../models/product/multilang-string"
import {IDropdownOption} from "../../ui/product-form/_reducers/_utils/product-form-types"
import {ITechFields} from "../../ui/product-form/_reducers/form-id-state"
import {
    Roles,
    caratechHasAnyValidValue,
    get_Only_DeclinableFromTechfields,
    getlangFromCache,
    isValidCaratechValue,
} from "../../utils"
import {deleteDoublons} from "../../utils/doublon-tools"
import {stringify} from "../../utils/stringifyier"

export const $getProduct = (
    formId: string,
    role: Roles
): Promise<ISupplierReference> => {
    const myHeaders = new Headers()
    return $myFetch(
        `product/${formId}`,
        {
            headers: myHeaders,
        },
        [],
        role
    )
        .then((res) => res.json())
        .then((res) => res[0])
        .then((res: ISupplierReference) => ({
            ...res,
            label: Multilang.String.CreateFrom(res.label),
            purchaseLabel: Multilang.String.CreateFrom(res.purchaseLabel),
            purchaseIntroduction: Multilang.String.CreateFrom(
                res.purchaseIntroduction
            ),
            buyingReasons: Multilang.String.CreateFrom(res.buyingReasons),
            techFields: res.techFields.map((v) =>
                v.type === CaratechType.text
                    ? {...v, value: Multilang.String.CreateFrom(v.value)}
                    : v
            ),
        }))
}

type PreviousVersionType = "declined" | "classic"
export const $getProductPreviousVersions = <T extends PreviousVersionType>(
    formId: string,
    dates: string[] | undefined,
    type: T,
    storeData: (
        previousVersion: T extends "declined"
            ? ISupplierDeclinedReference
            : ISupplierReference,
        date: string
    ) => void,
    signal: AbortSignal
): void => {
    if (!dates || dates.length <= 0 || signal.aborted) {
        return
    } else {
        const remaining = [...dates]
        const toFetch = remaining.pop()
        if (!toFetch) {
            return
        }
        const myHeaders = new Headers()
        const url =
            type === "declined"
                ? "product/previousDeclinedVersion/"
                : "product/previousVersion/"
        $myFetch(
            `${url}${formId}`,
            {
                headers: myHeaders,
                signal,
            },
            [{key: "validFrom", value: toFetch}],
            Roles.MARKET_LEADER
        )
            .then((res) => res.json())
            .then((res) => res[0])
            .then(
                (
                    res: T extends "declined"
                        ? ISupplierDeclinedReference
                        : ISupplierReference
                ) => ({
                    ...res,
                    label: Multilang.String.CreateFrom(res.label),
                    purchaseLabel: Multilang.String.CreateFrom(res.purchaseLabel),
                    buyingReasons: Multilang.String.CreateFrom(res.buyingReasons),
                    techFields: res.techFields.map((v) =>
                        v.type === CaratechType.text
                            ? {...v, value: Multilang.String.CreateFrom(v.value)}
                            : v
                    ),
                })
            )
            .then(
                (
                    res: T extends "declined"
                        ? ISupplierDeclinedReference
                        : ISupplierReference
                ) => {
                    storeData(res, toFetch)
                    $getProductPreviousVersions(
                        formId,
                        remaining,
                        type,
                        storeData,
                        signal
                    )
                }
            )
            .catch(() => {
            })
    }
}

export const $fetchDeclinedColors = (): Promise<IDropdownOption[]> => {
    const myHeaders = new Headers()
    return $myFetch("variant", {
        headers: myHeaders,
    }).then((res) => res.json())
}

interface Carac {
    codeEnum: string
    value: string
    valueCode: string
    lang: string
}

interface Variant {
    droopyId: string
    caracList: Carac[]
}

interface SanitizeVariantRequest {
    supplierName: string
    topRefDroopyId: string
    variants: Variant[]
}

export const $sanitizeVariants = (
    product: ISupplierReference | null
): Promise<object> => {
    if (!product || !product.isTopRef || !product.declined || product.declined.length <= 0)
        return Promise.resolve({})
    const payload: SanitizeVariantRequest = {
        supplierName: product.supplierName,
        topRefDroopyId: product.droopyId,
        variants: product.declined.map(d => ({
            droopyId: d.droopyId,
            caracList: d.techFields.map(t =>
                Array.isArray(t.value) && Multilang.String.IsMultilangString(t.value)
                    ? t.value
                        .filter(v => v.value)
                        .map(v => ({
                            codeEnum: t.codeEnum,
                            value: v.value,
                            valueCode: t.valueCode,
                            lang: v.lang
                        }))
                    : ({
                        codeEnum: t.codeEnum,
                        value: t.value,
                        valueCode: t.valueCode,
                        lang: t.lang
                    }) as Carac).flat()
        }) as Variant)
    }

    const myHeaders = new Headers()
    myHeaders.append("Content-Type", "application/json")
    return $myFetch(
        "product/sanitizeVariant",
        {
            body: JSON.stringify(payload),
            headers: myHeaders,
            method: "PUT",
        },
        []
    )
}

export const $addVariants = (
    product: ISupplierReference | null,
    _techFields: ITechFields
): Promise<object> => {
    const myHeaders = new Headers()
    myHeaders.append("enctype", "multipart/form-data")
    // myHeaders.append("content-type", "application/json")
    const form = new FormData()
    form.append("supplierName", product?.supplierName || "")
    form.append("droopyId", product?.droopyId || "")
    const techFields = JSON.parse(JSON.stringify(_techFields)) as ITechFields
    const declinableTechFields = get_Only_DeclinableFromTechfields(
        techFields,
        false
    )
    product?.declined?.forEach((declined) => {
        declined.techFields.forEach((caratech) => {
            if (
                declinableTechFields[caratech.codeEnum] &&
                caratechHasAnyValidValue(caratech)
            ) {
                const toAdd = isValidCaratechValue(caratech.valueCode)
                    ? caratech.valueCode
                    : caratech.value
                const accumulator = declinableTechFields[caratech.codeEnum].value
                if (Array.isArray(accumulator)) {
                    declinableTechFields[caratech.codeEnum].value = deleteDoublons([
                        ...accumulator.filter(isValidCaratechValue),
                        toAdd,
                    ])
                } else if (isValidCaratechValue(accumulator)) {
                    declinableTechFields[caratech.codeEnum].value = deleteDoublons([
                        accumulator,
                        toAdd,
                    ])
                } else {
                    declinableTechFields[caratech.codeEnum].value = [toAdd]
                }
            }
        })
    })

    const final = get_Only_DeclinableFromTechfields(techFields, true)

    Object.keys(final).forEach((key) => {
        const payload = {
            codeEnum: final[key].code,
            name: final[key].label,
            lang: getlangFromCache(),
            type: final[key].regenerateInfos.fieldType,
            declinableList: final[key].value,
        }
        form.append("rawDeclinableCaratechsJson", JSON.stringify(payload))
    })

    addToFormDataDeclinableLabels(form, techFields)
    return $myFetch("product/variant", {
        body: form,
        headers: myHeaders,
        method: "POST",
    })
}

export const $insertProduct = (
    form: FormData,
    techFields: ITechFields
): Promise<any> => {
    const myHeaders = new Headers()
    myHeaders.append("enctype", "multipart/form-data")
    addToFormDataDeclinableLabels(form, techFields)
    return $myFetch("product", {
        body: form,
        headers: myHeaders,
        method: "POST",
    })
}

const addToFormDataDeclinableLabels = (
    form: FormData,
    techFields: ITechFields
) => {
    const techFieldsDeclined = Object.keys(techFields).reduce(
        (acc, key) =>
            techFields[key].isDeclinable && Array.isArray(techFields[key].value)
                ? [
                    ...acc,
                    ...(techFields[key].value.map((v: any) => ({
                        codeEnum: key,
                        value: v,
                        text:
                            techFields[key].options.find((option) => option.value === v)
                                ?.text || v,
                    })) || []),
                ]
                : acc,
        [] as any
    )

    techFieldsDeclined.forEach((declined: any) =>
        form.append("rawDeclinedLabels", stringify(declined))
    )
}

export const $updateProduct = (
    form: FormData,
    oldSupplierRef: string
): Promise<any> => {
    const myHeaders = new Headers()
    myHeaders.append("enctype", "multipart/form-data")

    return $myFetch(
        "product",
        {
            body: form,
            headers: myHeaders,
            method: "PUT",
        },
        [{key: "oldSupplierRef", value: oldSupplierRef}]
    )
}

export const $updateDeclinedProduct = (
    form: FormData,
    oldSupplierRef: string
): Promise<any> => {
    const myHeaders = new Headers()
    myHeaders.append("enctype", "multipart/form-data")

    return $myFetch(
        "product/declined",
        {
            body: form,
            headers: myHeaders,
            method: "PUT",
        },
        [{key: "oldSupplierRef", value: oldSupplierRef}]
    )
}

export const $validateProduct = (
    id: string,
    role: Roles,
    declinedDroopyId: string | null = null,
    supplierName?: string
): Promise<Response> => {
    const myHeaders = new Headers()
    return $myFetch(
        "product/validate/" + id,
        {
            headers: myHeaders,
            method: "PUT",
        },
        [{key: "declinedDroopyId", value: declinedDroopyId}].filter(
            (x) => x.value
        ),
        role,
        supplierName
    )
}

export const $validateSeveralProducts = (
    ids: string[],
    role: Roles
): Promise<Response[]> => {
    const results = ids.map((id) => $validateProduct(id, role))
    return Promise.all(results)
}
